/**
 * ENG-313 — the one OI list. MQTT is preferred (it carries the drone, pilot and
 * live status); FlightBlender is the schedule-based source of truth that fills
 * in whenever MQTT has nothing to say.
 *
 *   • Blender OI, MQTT fresh        → the MQTT OI.
 *   • Blender OI, MQTT silent > N   → Blender's latest data, keeping the last
 *                                     drone / pilot fields MQTT gave us.
 *   • Blender OI, never on MQTT     → Blender's, "Scheduled" until a drone links.
 *   • MQTT-only OI                  → shown while fresh; once silent and Blender
 *                                     (reachable) no longer lists it, it's over.
 *
 * Linked by declaration id: Blender's `id` is the Custodian's
 * `utm_declaration_id` and the MQTT envelope's `oi_id`.
 */
import type { OiSummary } from '@/lib/api/types';
import type { DroneLive } from '@/lib/store/drones';
import { buildOiSummaryFromDrone } from './droneOi';

/** MQTT silence after which the latest Blender data is used instead. Blender is
 *  polled every 60 s, so 120 s tolerates a missed poll without flapping. */
export const OI_MQTT_STALE_MS = 120_000;

type MqttOi = { summary: OiSummary; fresh: boolean };

function inWindow(oi: OiSummary, now: number): boolean {
  const start = Date.parse(oi.start_utc);
  const end = Date.parse(oi.end_utc);
  return Number.isFinite(start) && Number.isFinite(end) && start <= now && now < end;
}

export function composeOis(input: {
  /** REST list (demo mode only) — its OIs win, as before. */
  rest: OiSummary[];
  /** Blender OIs; `undefined` while loading or unreachable → MQTT-only. */
  blender: OiSummary[] | undefined;
  drones: DroneLive[];
  now: number;
  staleMs?: number;
}): OiSummary[] {
  const { rest, blender, drones, now, staleMs = OI_MQTT_STALE_MS } = input;
  const restIds = new Set(rest.map((o) => o.oi_id));
  const restAircraft = new Set(rest.map((o) => o.aircraft));

  const mqtt = new Map<string, MqttOi>();
  for (const d of drones) {
    const summary = buildOiSummaryFromDrone(d);
    if (!summary || restIds.has(summary.oi_id) || restAircraft.has(summary.aircraft)) continue;
    const at = d.oi ? Date.parse(d.oi.received_at) : Number.NaN;
    mqtt.set(summary.oi_id, { summary, fresh: Number.isFinite(at) && now - at <= staleMs });
  }

  const out: OiSummary[] = [...rest];
  const used = new Set<string>();

  for (const b of blender ?? []) {
    if (restIds.has(b.oi_id) || !inWindow(b, now)) continue;
    const m = mqtt.get(b.oi_id);
    used.add(b.oi_id);
    if (m?.fresh) {
      out.push(m.summary);
    } else if (m) {
      const s = m.summary;
      out.push({
        ...b,
        title: s.title || b.title,
        aircraft: s.aircraft,
        drone_model: s.drone_model,
        pilot_name: s.pilot_name,
        ops_approval: s.ops_approval,
        op_type: s.op_type,
        legal_rule: s.legal_rule,
        origin: s.origin ?? b.origin,
        partner_tenant: s.partner_tenant ?? b.partner_tenant,
      });
    } else {
      out.push(b.status === 'Accepted' ? { ...b, status: 'Scheduled' } : b);
    }
  }

  for (const [id, m] of mqtt) {
    if (used.has(id)) continue;
    // Blender reachable + MQTT silent + not listed ⇒ ended or cancelled.
    if (m.fresh || blender === undefined) out.push(m.summary);
  }
  return out;
}
